/**
 * Billing Overview Page (Admin Portal)
 *
 * Admin-level view of all companies' subscription statuses
 * Features:
 * - MRR/ARR summary cards
 * - Subscription state breakdown
 * - Revenue by tier visualization
 * - Company list with subscription status
 * - Filter by tier and state
 * - Churn tracking section
 */

import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Link } from 'react-router-dom';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  Users,
  Filter,
  PieChart,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Tier definitions matching shared-schemas
const TIER_DEFINITIONS = {
  plus: {
    displayName: 'VALUTTEN Plus',
    pricing: { monthly: 49700, annual: 497000 }
  },
  pro: {
    displayName: 'VALUTTEN Pro',
    pricing: { monthly: 79700, annual: 797000 }
  },
  enterprise: {
    displayName: 'VALUTTEN Enterprise',
    pricing: { monthly: 199700, annual: 1997000 }
  }
};

type TierKey = 'plus' | 'pro' | 'enterprise';
type StateKey = 'active' | 'grace_period' | 'suspended' | 'churned' | 'pending_setup' | 'onboarding';

// Types
interface CompanySubscription {
  companyId: string;
  companyName: string;
  tier: string;
  state: string;
  monthlyRevenue: number;
  billingPeriod: string;
  billingStatus: string;
  nextBillingDate: any;
  daysUntilBilling: number;
  churnedAt?: any;
  gracePeriodStartedAt?: any;
}

// Helper to format currency
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(cents / 100);
};

// Revenue by tier chart component (simple bar visualization)
const RevenueByTierChart: React.FC<{ data: { tier: string; mrr: number; count: number }[] }> = ({ data }) => {
  const maxMRR = Math.max(...data.map(d => d.mrr), 1);

  const getTierColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'plus': return 'from-blue-500 to-blue-600';
      case 'pro': return 'from-purple-500 to-purple-600';
      case 'enterprise': return 'from-amber-500 to-amber-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <div className="space-y-4">
      {data.map((item) => (
        <div key={item.tier} className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-300 font-medium">
              {TIER_DEFINITIONS[item.tier.toLowerCase() as TierKey]?.displayName || item.tier}
            </span>
            <span className="text-white font-semibold">
              {formatCurrency(item.mrr)} ({item.count} companies)
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-3">
            <div
              className={`h-3 rounded-full bg-gradient-to-r ${getTierColor(item.tier)} transition-all`}
              style={{ width: `${(item.mrr / maxMRR) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// State distribution component
const StateDistribution: React.FC<{ stats: Record<string, number> }> = ({ stats }) => {
  const stateConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    active: { label: 'Active', color: 'bg-green-500', icon: <CheckCircle size={16} /> },
    grace_period: { label: 'Grace Period', color: 'bg-yellow-500', icon: <Clock size={16} /> },
    suspended: { label: 'Suspended', color: 'bg-red-500', icon: <AlertTriangle size={16} /> },
    churned: { label: 'Churned', color: 'bg-slate-500', icon: <XCircle size={16} /> },
    pending_setup: { label: 'Pending Setup', color: 'bg-blue-500', icon: <Clock size={16} /> },
    onboarding: { label: 'Onboarding', color: 'bg-cyan-500', icon: <RefreshCw size={16} /> }
  };

  const total = Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-3">
      {Object.entries(stats).map(([state, count]) => {
        const config = stateConfig[state] || { label: state, color: 'bg-slate-500', icon: null };
        const percentage = total > 0 ? (count / total) * 100 : 0;

        return (
          <div key={state} className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${config.color}`} />
            <span className="text-slate-300 text-sm flex-grow">{config.label}</span>
            <span className="text-white font-medium">{count}</span>
            <span className="text-slate-400 text-xs w-12 text-right">
              {percentage.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const BillingPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanySubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'companyName' | 'monthlyRevenue' | 'daysUntilBilling'>('companyName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadCompanySubscriptions();
  }, []);

  const loadCompanySubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all companies
      const companiesSnapshot = await getDocs(collection(db, 'companies'));

      const subscriptionPromises = companiesSnapshot.docs.map(async (companyDoc) => {
        const companyData = companyDoc.data();
        const companyId = companyDoc.id;

        try {
          const subSnapshot = await getDocs(
            collection(db, `companies/${companyId}/subscription`)
          );

          if (subSnapshot.empty) {
            return {
              companyId,
              companyName: companyData.name || 'Unknown',
              tier: 'none',
              state: 'no_subscription',
              monthlyRevenue: 0,
              billingPeriod: 'none',
              billingStatus: 'N/A',
              nextBillingDate: null,
              daysUntilBilling: 0,
            };
          }

          const subscription = subSnapshot.docs[0].data();
          const billing = subscription.billing || {};

          // Calculate days until billing
          let daysUntilBilling = 0;
          if (billing.nextBillingDate) {
            const nextDate = billing.nextBillingDate.toDate();
            const now = new Date();
            daysUntilBilling = Math.ceil((nextDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          }

          // Calculate monthly revenue based on billing period
          let monthlyRevenue = billing.amount || 0;
          if (billing.period === 'annual' && monthlyRevenue > 0) {
            monthlyRevenue = Math.round(monthlyRevenue / 12);
          } else if (billing.period === 'quarterly' && monthlyRevenue > 0) {
            monthlyRevenue = Math.round(monthlyRevenue / 3);
          }

          return {
            companyId,
            companyName: companyData.name || 'Unknown',
            tier: subscription.tier || 'unknown',
            state: subscription.state || 'unknown',
            monthlyRevenue,
            billingPeriod: billing.period || 'monthly',
            billingStatus: billing.status || 'unknown',
            nextBillingDate: billing.nextBillingDate,
            daysUntilBilling,
            churnedAt: subscription.churnedAt,
            gracePeriodStartedAt: subscription.gracePeriodStartedAt
          };
        } catch (err) {
          console.error(`Error loading subscription for ${companyId}:`, err);
          return {
            companyId,
            companyName: companyData.name || 'Unknown',
            tier: 'error',
            state: 'error',
            monthlyRevenue: 0,
            billingPeriod: 'none',
            billingStatus: 'Error',
            nextBillingDate: null,
            daysUntilBilling: 0,
          };
        }
      });

      const subscriptions = await Promise.all(subscriptionPromises);
      setCompanies(subscriptions);

    } catch (err: any) {
      setError(err.message || 'Failed to load subscription data');
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Computed stats
  const stats = useMemo(() => {
    const activeCompanies = companies.filter(c => c.state === 'active');
    const totalMRR = activeCompanies.reduce((sum, s) => sum + s.monthlyRevenue, 0);
    const totalARR = totalMRR * 12;

    // State counts
    const stateCounts: Record<string, number> = {};
    companies.forEach(c => {
      const state = c.state || 'unknown';
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    });

    // Revenue by tier
    const revenueByTier: { tier: string; mrr: number; count: number }[] = [];
    const tierGroups: Record<string, { mrr: number; count: number }> = {};
    activeCompanies.forEach(c => {
      const tier = c.tier?.toLowerCase() || 'unknown';
      if (!tierGroups[tier]) {
        tierGroups[tier] = { mrr: 0, count: 0 };
      }
      tierGroups[tier].mrr += c.monthlyRevenue;
      tierGroups[tier].count += 1;
    });
    Object.entries(tierGroups).forEach(([tier, data]) => {
      revenueByTier.push({ tier, ...data });
    });
    // Sort by MRR descending
    revenueByTier.sort((a, b) => b.mrr - a.mrr);

    // Churned companies (last 90 days)
    const churnedRecently = companies.filter(c => {
      if (c.state !== 'churned' || !c.churnedAt) return false;
      const churnDate = c.churnedAt.toDate ? c.churnedAt.toDate() : new Date(c.churnedAt);
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      return churnDate >= ninetyDaysAgo;
    });

    return {
      totalMRR,
      totalARR,
      activeCount: stateCounts.active || 0,
      gracePeriodCount: stateCounts.grace_period || 0,
      suspendedCount: stateCounts.suspended || 0,
      churnedCount: stateCounts.churned || 0,
      stateCounts,
      revenueByTier,
      churnedRecently
    };
  }, [companies]);

  // Filtered and sorted companies
  const filteredCompanies = useMemo(() => {
    let result = [...companies];

    if (tierFilter !== 'all') {
      result = result.filter(c => c.tier?.toLowerCase() === tierFilter);
    }

    if (stateFilter !== 'all') {
      result = result.filter(c => c.state === stateFilter);
    }

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'companyName':
          comparison = a.companyName.localeCompare(b.companyName);
          break;
        case 'monthlyRevenue':
          comparison = a.monthlyRevenue - b.monthlyRevenue;
          break;
        case 'daysUntilBilling':
          comparison = a.daysUntilBilling - b.daysUntilBilling;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [companies, tierFilter, stateFilter, sortField, sortDirection]);

  // State badge color
  const getStateBadgeColor = (state: string) => {
    switch (state) {
      case 'active':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'grace_period':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'suspended':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'churned':
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
      case 'pending_setup':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'onboarding':
        return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
      case 'no_subscription':
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  // Tier badge color
  const getTierBadgeColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'plus':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'pro':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'enterprise':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900">
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-slate-400">Loading billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="p-8">
        {/* Modern Gradient Header */}
        <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-500 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl animate-pulse delay-700" />
          </div>
          <div className="relative">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-10 h-10 text-white" />
                  <h1 className="text-4xl font-bold text-white">Billing Overview</h1>
                </div>
                <p className="text-white/90 text-lg mb-4">Monitor all company subscriptions and revenue</p>
                <div className="flex gap-4 text-white/80 text-sm">
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    MRR & ARR Tracking
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {stats.activeCount} Active Subscriptions
                  </span>
                  <span className="flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Revenue Analytics
                  </span>
                </div>
              </div>
              <button
                onClick={loadCompanySubscriptions}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-xl font-semibold transition-all border border-white/30 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-300">Error Loading Billing Data</h3>
                <p className="mt-1 text-sm text-red-400">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* MRR/ARR Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/30">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-emerald-400" />
              <span className="text-xs text-emerald-300 uppercase tracking-wider">MRR</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {formatCurrency(stats.totalMRR)}
            </p>
            <p className="text-sm text-emerald-200 mt-1">Monthly Recurring Revenue</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/30">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-cyan-400" />
              <span className="text-xs text-cyan-300 uppercase tracking-wider">ARR</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {formatCurrency(stats.totalARR)}
            </p>
            <p className="text-sm text-cyan-200 mt-1">Annual Recurring Revenue</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-500/30">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <span className="text-xs text-green-300 uppercase tracking-wider">Active</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.activeCount}</p>
            <p className="text-sm text-green-200 mt-1">Active Subscriptions</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 backdrop-blur-sm rounded-xl p-6 border border-yellow-500/30">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              <span className="text-xs text-yellow-300 uppercase tracking-wider">At Risk</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {stats.gracePeriodCount + stats.suspendedCount}
            </p>
            <p className="text-sm text-yellow-200 mt-1">Grace Period + Suspended</p>
          </div>
        </div>

        {/* Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue by Tier */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <PieChart className="w-5 h-5 text-emerald-400" />
                Revenue by Tier
              </h2>
            </div>
            {stats.revenueByTier.length > 0 ? (
              <RevenueByTierChart data={stats.revenueByTier} />
            ) : (
              <p className="text-slate-400 text-center py-8">No revenue data available</p>
            )}
          </div>

          {/* Subscription State Distribution */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-400" />
                Subscription States
              </h2>
            </div>
            <StateDistribution stats={stats.stateCounts} />
          </div>
        </div>

        {/* Churn Tracking Section */}
        {stats.churnedRecently.length > 0 && (
          <div className="bg-red-500/10 backdrop-blur-sm rounded-xl p-6 border border-red-500/30 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-400" />
              <h2 className="text-lg font-semibold text-red-300">Recent Churn (Last 90 Days)</h2>
              <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-300 text-xs rounded-full">
                {stats.churnedRecently.length} companies
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.churnedRecently.slice(0, 6).map(company => (
                <Link
                  key={company.companyId}
                  to={`/companies/${company.companyId}`}
                  className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 hover:border-red-500/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-white font-medium">{company.companyName}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-400">
                    Was paying {formatCurrency(company.monthlyRevenue)}/mo
                  </div>
                  <div className="mt-1 text-xs text-red-400">
                    Churned {company.churnedAt?.toDate ?
                      company.churnedAt.toDate().toLocaleDateString() :
                      'recently'}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filter by:</span>
            </div>

            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Tiers</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>

            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All States</option>
              <option value="active">Active</option>
              <option value="grace_period">Grace Period</option>
              <option value="suspended">Suspended</option>
              <option value="churned">Churned</option>
              <option value="pending_setup">Pending Setup</option>
              <option value="onboarding">Onboarding</option>
            </select>

            <div className="flex-grow" />

            <span className="text-sm text-slate-400">
              Showing {filteredCompanies.length} of {companies.length} companies
            </span>
          </div>
        </div>

        {/* Companies Table */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-700/50">
            <h2 className="text-xl font-semibold text-white">Company Subscriptions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700/50">
              <thead className="bg-slate-800/80">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('companyName')}
                  >
                    <div className="flex items-center gap-1">
                      Company
                      <SortIcon field="companyName" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('monthlyRevenue')}
                  >
                    <div className="flex items-center gap-1">
                      MRR
                      <SortIcon field="monthlyRevenue" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Billing
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('daysUntilBilling')}
                  >
                    <div className="flex items-center gap-1">
                      Next Billing
                      <SortIcon field="daysUntilBilling" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-slate-800/30 divide-y divide-slate-700/50">
                {filteredCompanies.map((company) => (
                  <tr key={company.companyId} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-slate-500 mr-3" />
                        <div className="text-sm font-medium text-white">{company.companyName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTierBadgeColor(company.tier)}`}>
                        {company.tier === 'none' ? 'No Sub' :
                          TIER_DEFINITIONS[company.tier?.toLowerCase() as TierKey]?.displayName ||
                          company.tier.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStateBadgeColor(company.state)}`}>
                        {company.state.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatCurrency(company.monthlyRevenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      <span className={`capitalize ${
                        company.billingPeriod === 'annual' ? 'text-green-400' : ''
                      }`}>
                        {company.billingPeriod}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {company.nextBillingDate ? (
                        <>
                          {new Date(company.nextBillingDate.toDate()).toLocaleDateString()}
                          <span className={`text-xs ml-1 ${
                            company.daysUntilBilling <= 7 ? 'text-yellow-400' : 'text-slate-500'
                          }`}>
                            ({company.daysUntilBilling}d)
                          </span>
                        </>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/companies/${company.companyId}`}
                        className="text-emerald-400 hover:text-emerald-300 transition-colors"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {filteredCompanies.length === 0 && !loading && !error && (
          <div className="text-center py-12 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 mt-6">
            <Building2 className="mx-auto h-12 w-12 text-slate-600" />
            <h3 className="mt-2 text-sm font-medium text-slate-300">No companies found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {tierFilter !== 'all' || stateFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Get started by onboarding your first company.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
